import { ChevronDown, Droplet, Hammer, HardHat, Zap } from 'lucide-react';
import { useState } from 'react';

import { COLORS } from './constants';
import { ScrollHintList } from './ScrollOverflowHint';

import { HauserEQPermit } from '@/services/api/hauser-eq/api';

export interface PermitHistoryCardProps {
  permits: HauserEQPermit[];
  /** Whether the accordion starts open. Defaults false, matching the
   *  other pillar/disclosure accordions on this page. */
  defaultOpen?: boolean;
}

const formatDate = (iso: string | undefined): string | null => {
  if (!iso) return null;
  // Parse the Y/M/D components directly rather than `new Date(iso)` —
  // a bare date string like "2025-03-28" parses as UTC midnight, which
  // toLocaleDateString then renders a day early in any timezone behind
  // UTC (i.e. all of the US).
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  const [, year, month, day] = match;
  const d = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/** Most permits arrive with `issued_date` null even when finalized —
 *  `last_status_date` is the reliably-populated field, so prefer it. */
const permitDate = (p: HauserEQPermit): string | null =>
  formatDate(p.last_status_date) ?? formatDate(p.issued_date) ?? formatDate(p.applied_date);

/** PropertyLens's `type`/`work_description` fields arrive as raw,
 *  inconsistently-cased department shorthand (e.g. "Sgsov", mixed casing
 *  across permits on the same property). Expand terms we've actually
 *  confirmed rather than guessing — permit abbreviations vary by county,
 *  so this grows opportunistically as new ones show up in real data. */
const KNOWN_TERMS: Record<string, string> = {
  sgsov: 'Seismic Gas Shut-Off Valve',
  'bldg-alter/repair': 'Building Alteration/Repair',
};

/** Capitalizes every letter-run in place, so unrecognized values are at
 *  least consistently cased instead of "Electrical" next to "plumbing". */
const titleCase = (s: string): string =>
  s.replace(/[a-zA-Z]+/g, (word) => word[0].toUpperCase() + word.slice(1).toLowerCase());

/** For short, category-like fields (type, subtype) — glossary lookup,
 *  else title-cased so casing is at least consistent across permits. */
const humanizeLabel = (raw: string | undefined): string | null => {
  if (!raw) return null;
  const known = KNOWN_TERMS[raw.trim().toLowerCase()];
  return known ?? titleCase(raw);
};

/** For freeform description text — glossary lookup only. Most
 *  descriptions are already reasonably-formed sentences ("Remodel to (e)
 *  single-family dwelling"); title-casing the whole thing would make
 *  those look worse, not better. Only expand it when it's literally a
 *  known department abbreviation standing in for the whole description. */
const humanizeDescription = (raw: string | undefined): string | null => {
  if (!raw) return null;
  return KNOWN_TERMS[raw.trim().toLowerCase()] ?? raw;
};

/** Best-effort icon per permit type — falls back to a generic hard hat
 *  when the type doesn't match a known trade. Matched on substrings since
 *  PropertyLens type strings aren't a fixed enum ("Electrical" vs
 *  "electrical panel" vs "ELEC" have all been seen across properties). */
const iconForType = (type: string | undefined) => {
  const t = (type ?? '').toLowerCase();
  if (t.includes('electric')) return Zap;
  if (t.includes('plumb')) return Droplet;
  if (t.includes('bldg') || t.includes('build') || t.includes('alter') || t.includes('structural'))
    return Hammer;
  return HardHat;
};

/**
 * Renders a property's raw building-permit history as a collapsible
 * list. Mirrors NHDDisclosuresCard's accordion visual language so it
 * reads as another pillar card, not a foreign block. No scoring impact —
 * this is disclosure/history, same framing as the NHD card.
 */
// The collapsed card's snapshot: a hairline across the years on file with a dot per permit, and the range beside it
const permitYear = (p: HauserEQPermit): number | null => {
  const raw = p.last_status_date ?? p.issued_date ?? p.applied_date;
  const y = raw ? Number(String(raw).slice(0, 4)) : NaN;
  return Number.isFinite(y) && y > 1900 ? y : null;
};

const PermitTimeline = ({ permits }: { permits: HauserEQPermit[] }) => {
  const years = permits.map(permitYear).filter((y): y is number => y != null);
  if (!years.length) return null;
  const min = Math.min(...years);
  const max = Math.max(...years);
  const span = Math.max(1, max - min);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} aria-label={`Permits from ${min} to ${max}`}>
      <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)' }}>{min}</span>
      <span style={{ position: 'relative', width: 96, height: 8 }}>
        <span style={{ position: 'absolute', left: 0, right: 0, top: 4, height: 1, background: 'var(--tx-line)' }} />
        {years.map((y, i) => (
          <span
            key={i}
            style={{
              position: 'absolute',
              top: 1,
              left: `calc(${((y - min) / span) * 100}% - 3px)`,
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--tx-text)',
            }}
          />
        ))}
      </span>
      {max !== min && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)' }}>{max}</span>}
    </div>
  );
};

export const PermitHistoryCard = ({ permits, defaultOpen = false }: PermitHistoryCardProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  if (!permits.length) return null;

  return (
    <div
      style={{
        background: COLORS.card,
        backgroundImage: 'var(--tx-surface-light)',
        boxShadow: 'var(--tx-shadow)',
        borderRadius: '24px',
        overflow: 'hidden',
      }}>
      <div
        onClick={() => setIsOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          height: 60,
          cursor: 'pointer',
          gap: 10,
        }}>
        <span style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: 'var(--tx-text)', whiteSpace: 'nowrap' }}>
          Permit history
        </span>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--tx-faint)', whiteSpace: 'nowrap' }}>
          {permits.length}
        </span>
        <div style={{ flex: 1 }} />
        <PermitTimeline permits={permits} />
        <span
          style={{
            color: COLORS.textDim,
            display: 'inline-flex',
            alignItems: 'center',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.18s ease',
          }}>
          <ChevronDown size={14} />
        </span>
      </div>

      {isOpen && (
        <div style={{ padding: '0 20px 20px' }}>
          <style>{`
            .permit-history-scroll::-webkit-scrollbar { display: none; }
          `}</style>
          <div style={{ fontSize: 11, color: COLORS.textDim, lineHeight: 1.55, marginBottom: 8 }}>
            Building permits on file for this property, via public MLS records. Not factored
            into the score.
          </div>
          {/* Capped to ~3 rows visible — scrollbar hidden, bouncing chevron
              hints at more content instead. */}
          <ScrollHintList
            className="permit-history-scroll"
            style={{
              maxHeight: 245,
              overflowY: 'auto',
              scrollbarWidth: 'none',
            }}>
            {permits.map((permit, i) => (
              <PermitRow
                key={`${permit.number ?? 'permit'}-${i}`}
                permit={permit}
                isLast={i === permits.length - 1}
              />
            ))}
          </ScrollHintList>
        </div>
      )}
    </div>
  );
};

function PermitRow({ permit }: { permit: HauserEQPermit; isLast: boolean }) {
  const date = permitDate(permit);
  const contractor = permit.contractors?.[0]?.name;
  const type = humanizeLabel(permit.type) ?? 'Permit';
  const description = humanizeDescription(permit.work_description);
  const Icon = iconForType(permit.type);

  return (
    <div
      style={{
        padding: '12px 0',
        borderTop: '1px solid var(--tx-line)',
      }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            width: 18,
            height: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: 1,
          }}>
          <Icon size={16} color={COLORS.textDim} strokeWidth={1.75} aria-hidden />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                fontWeight: 600,
                color: COLORS.text,
              }}>
              {type}
            </span>
            {permit.status && (
              <span
                style={{
                  flexShrink: 0,
                  fontSize: 10,
                  fontWeight: 600,
                  color: COLORS.card,
                  background: COLORS.accent2,
                  padding: '3px 10px',
                  borderRadius: 6,
                }}>
                {permit.status}
              </span>
            )}
          </div>
          {description && (
            <div style={{ marginTop: 4, fontSize: 12, color: COLORS.textDim, lineHeight: 1.4 }}>
              {description}
            </div>
          )}
          <div
            style={{
              marginTop: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
              color: COLORS.textDim,
              opacity: 0.8,
            }}>
            {date && <span>{date}</span>}
            {date && contractor && <span>·</span>}
            {contractor && <span>{contractor}</span>}
            {permit.number && (date || contractor) && <span>·</span>}
            {permit.number && <span>#{permit.number}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PermitHistoryCard;
